import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import api from "../lib/api";
import { toast } from "sonner";

export default function Register() {
    const { setUser } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const [email, setEmail] = useState("");
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [referralCode, setReferralCode] = useState(() => {
        const fromQuery = (params.get("ref") || "").toUpperCase();
        if (fromQuery) return fromQuery;
        try {
            return (localStorage.getItem("footsie_referral_code") || "").toUpperCase();
        } catch (_e) {
            return "";
        }
    });
    const [isAdult, setIsAdult] = useState(false);
    const [agreedTerms, setAgreedTerms] = useState(false);
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!isAdult) { toast.error("You must be 18 or older to join"); return; }
        if (!agreedTerms) { toast.error("Please agree to the Terms and Privacy Policy"); return; }
        setLoading(true);
        try {
            const { data } = await api.post("/auth/register", {
                email,
                password,
                username,
                referral_code: referralCode || undefined,
                is_adult: isAdult,
                accepted_terms_version: (await api.get("/legal/version").catch(() => ({ data: {} }))).data.terms_version,
            });
            if (data.token) localStorage.setItem("feetrate_token", data.token);
            setUser(data.user);
            if (referralCode && data.user.boost_credits > 0) {
                toast.success(`Welcome! You got a free Post Boost 🚀`);
            } else {
                toast.success("Profile created!");
            }
            navigate("/");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <main className="max-w-md mx-auto px-4 py-12">
            <div className="bg-white brut-static rounded-xl p-6 sm:p-8">
                <h1 className="display text-3xl font-black mb-1">Join the pod.</h1>
                <p className="text-sm text-[#4A4A4A] mb-6">Free to join. SFW only.</p>
                <form onSubmit={submit} className="space-y-4" data-testid="register-form">
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wide">Username</label>
                        <input
                            value={username}
                            onChange={(e) => setUsername(e.target.value.replace(/[^a-zA-Z0-9_]/g, ""))}
                            required
                            minLength={2}
                            maxLength={30}
                            className="w-full mt-1 px-4 py-3 rounded-lg brut-inset bg-white font-medium focus:outline-none focus:shadow-[3px_3px_0px_#0A0A0A]"
                            data-testid="register-username"
                        />
                    </div>
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wide">Email</label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            className="w-full mt-1 px-4 py-3 rounded-lg brut-inset bg-white font-medium focus:outline-none focus:shadow-[3px_3px_0px_#0A0A0A]"
                            data-testid="register-email"
                        />
                    </div>
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wide">Password</label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            minLength={6}
                            className="w-full mt-1 px-4 py-3 rounded-lg brut-inset bg-white font-medium focus:outline-none focus:shadow-[3px_3px_0px_#0A0A0A]"
                            data-testid="register-password"
                        />
                    </div>
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wide">Referral code (optional)</label>
                        <input
                            value={referralCode}
                            onChange={(e) => setReferralCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
                            maxLength={8}
                            placeholder="Add a friend's code for a free boost"
                            className="w-full mt-1 px-4 py-3 rounded-lg brut-inset bg-[#FFFDF7] font-bold uppercase tracking-widest focus:outline-none focus:shadow-[3px_3px_0px_#0A0A0A]"
                            data-testid="register-referral"
                        />
                        {referralCode && (
                            <div className="text-[11px] font-medium text-[#F0E9EC]/85 mt-1">
                                You&apos;ll both get 1 Post Boost credit 🚀
                            </div>
                        )}
                    </div>
                    <div className="space-y-2 pt-1">
                        <label className="flex items-start gap-2 text-xs font-medium cursor-pointer" data-testid="age-gate-label">
                            <input
                                type="checkbox"
                                checked={isAdult}
                                onChange={(e) => setIsAdult(e.target.checked)}
                                className="mt-1 w-4 h-4 border-2 border-[#0A0A0A]"
                                data-testid="age-gate-checkbox"
                            />
                            <span>I confirm I am at least <b>18 years old</b> (or the legal age of majority in my jurisdiction).</span>
                        </label>
                        <label className="flex items-start gap-2 text-xs font-medium cursor-pointer" data-testid="terms-agree-label">
                            <input
                                type="checkbox"
                                checked={agreedTerms}
                                onChange={(e) => setAgreedTerms(e.target.checked)}
                                className="mt-1 w-4 h-4 border-2 border-[#0A0A0A]"
                                data-testid="terms-agree-checkbox"
                            />
                            <span>
                                I agree to the{" "}
                                <Link to="/terms" target="_blank" className="underline font-bold">Terms of Service</Link>,{" "}
                                <Link to="/privacy" target="_blank" className="underline font-bold">Privacy Policy</Link>, and{" "}
                                <Link to="/guidelines" target="_blank" className="underline font-bold">Community Guidelines</Link>.
                            </span>
                        </label>
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full px-4 py-3 rounded-lg brut bg-[#FFB8D1] font-bold disabled:opacity-50"
                        data-testid="register-submit"
                    >
                        {loading ? "Creating…" : "Create profile"}
                    </button>
                </form>
                <p className="text-sm mt-5 text-center font-medium">
                    Already have an account?{" "}
                    <Link to="/login" className="font-bold underline" data-testid="register-to-login">
                        Log in
                    </Link>
                </p>
            </div>
        </main>
    );
}
